import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
}

interface LeadData {
  email: string;
  organizationName: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  phoneNumber?: string;
  companySize?: string;
}

interface OnboardingState {
  currentStep: number;
  steps: OnboardingStep[];
  loading: boolean;
  error: string | null;
  isComplete: boolean;
  leadData: LeadData | null;
  onboardingFlowId: string | null;
}

const initialState: OnboardingState = {
  currentStep: 0,
  steps: [
    {
      id: "lead-capture",
      title: "Get Started",
      description: "Enter your email and organization details",
      completed: false,
      required: true,
    },
    {
      id: "plan-selection",
      title: "Choose Plan",
      description: "Select a plan and number of seats",
      completed: false,
      required: true,
    },
    {
      id: "payment",
      title: "Payment",
      description: "Complete your payment",
      completed: false,
      required: true,
    },
    {
      id: "org-setup",
      title: "Organization Setup",
      description: "Configure your organization",
      completed: false,
      required: true,
    },
    {
      id: "complete",
      title: "Complete",
      description: "Setup complete",
      completed: false,
      required: true,
    },
  ],
  loading: false,
  error: null,
  isComplete: false,
  leadData: null,
  onboardingFlowId: null,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    completeStep: (state, action: PayloadAction<string>) => {
      const step = state.steps.find(s => s.id === action.payload);
      if (step) {
        step.completed = true;
      }
    },
    nextStep: (state) => {
      if (state.currentStep < state.steps.length - 1) {
        state.currentStep += 1;
      }
    },
    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },
    setLeadData: (state, action: PayloadAction<LeadData>) => {
      state.leadData = action.payload;
    },
    setOnboardingFlowId: (state, action: PayloadAction<string>) => {
      state.onboardingFlowId = action.payload;
    },
    completeOnboarding: (state) => {
      state.isComplete = true;
    },
    resetOnboarding: (state) => {
      state.currentStep = 0;
      state.steps.forEach(step => {
        step.completed = false;
      });
      state.isComplete = false;
      state.leadData = null;
      state.onboardingFlowId = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setCurrentStep,
  completeStep,
  nextStep,
  previousStep,
  setLeadData,
  setOnboardingFlowId,
  completeOnboarding,
  resetOnboarding,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
export type { LeadData };